import { resolveEntryTiming, type TimeReportPeriod, type TimeTotals, weekStart } from '@fos/shared/contracts/time';
import { type CurrencyAmount, formatAmounts } from '@fos/shared/money';
import { addDaysLocal, diffDaysLocal, type LocalDate } from '@fos/shared/time';
import { formatClock } from '../../lib/format';

/**
 * Formatowanie i drobne obliczenia modułu Czas (web). Czas i kwoty liczy serwer; tu tylko podgląd w formularzu
 * (ta sama funkcja `resolveEntryTiming` co w usłudze) i teksty po polsku jak w Figmie 06 Czas.
 */

/** Skróty dni jak w Figmie 06 („Wczoraj · sob 3.10”, „Pt 2.10”). */
const WEEKDAYS = ['nd', 'pn', 'wt', 'śr', 'czw', 'pt', 'sob'] as const;
const MONTHS_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'] as const;
/** „we wrześniu” (kafelek „Łącznie”). */
const MONTHS_LOCATIVE = [
  'w styczniu',
  'w lutym',
  'w marcu',
  'w kwietniu',
  'w maju',
  'w czerwcu',
  'w lipcu',
  'w sierpniu',
  'we wrześniu',
  'w październiku',
  'w listopadzie',
  'w grudniu',
] as const;
const MONTHS_GENITIVE = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
] as const;

function parts(date: LocalDate): [number, number, number] {
  return date.split('-').map(Number) as [number, number, number];
}

function weekday(date: LocalDate): number {
  const [y, m, d] = parts(date);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

const capitalize = (s: string) => s.charAt(0).toLocaleUpperCase('pl-PL') + s.slice(1);

/** „sob 3.10” */
export function shortDay(date: LocalDate): string {
  const [, m, d] = parts(date);
  return `${WEEKDAYS[weekday(date)]} ${d}.${String(m).padStart(2, '0')}`;
}

/** Nagłówek dnia: „Dziś · nd 4.10”, „Wczoraj · sob 3.10”, „Pt 2.10”, starsze lata z rokiem. */
export function dayLabel(date: LocalDate, today: LocalDate): string {
  if (date === today) return `Dziś · ${shortDay(date)}`;
  if (date === addDaysLocal(today, -1)) return `Wczoraj · ${shortDay(date)}`;
  if (date === addDaysLocal(today, 1)) return `Jutro · ${shortDay(date)}`;
  const label = capitalize(shortDay(date));
  return date.slice(0, 4) === today.slice(0, 4) ? label : `${label}.${date.slice(0, 4)}`;
}

/** Krótka data w pasku dodawania: „Dziś”, „Wczoraj”, „pt 2.10”. */
export function chipDay(date: LocalDate, today: LocalDate): string {
  if (date === today) return 'Dziś';
  if (date === addDaysLocal(today, -1)) return 'Wczoraj';
  if (date === addDaysLocal(today, 1)) return 'Jutro';
  return shortDay(date);
}

/** Nagłówek tygodnia: „Ten tydzień”, „Poprzedni tydzień”, „21–27.09”. */
export function weekLabel(start: LocalDate, today: LocalDate): string {
  const current = weekStart(today);
  if (start === current) return 'Ten tydzień';
  if (start === addDaysLocal(current, -7)) return 'Poprzedni tydzień';
  return rangeLabel({ from: start, to: addDaysLocal(start, 6) });
}

/** „1–30.09”, „28.09–4.10”, z rokiem gdy zakres wychodzi poza bieżący rok. */
export function rangeLabel(range: { from: LocalDate; to: LocalDate }, today?: LocalDate): string {
  const [fy, fm, fd] = parts(range.from);
  const [ty, tm, td] = parts(range.to);
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = today ? Number(today.slice(0, 4)) : ty;
  const suffix = fy !== year || ty !== year ? `.${ty}` : '';
  if (range.from === range.to) return `${fd}.${pad(fm)}${suffix}`;
  if (fy !== ty) return `${fd}.${pad(fm)}.${fy}–${td}.${pad(tm)}.${ty}`;
  if (fm !== tm) return `${fd}.${pad(fm)}–${td}.${pad(tm)}${suffix}`;
  return `${fd}–${td}.${pad(tm)}${suffix}`;
}

/** Zakres raportu słownie: „1 – 30 września 2026”, „28 września – 4 października 2026”. */
export function rangeLongLabel(range: { from: LocalDate; to: LocalDate }): string {
  const [fy, fm, fd] = parts(range.from);
  const [ty, tm, td] = parts(range.to);
  if (range.from === range.to) return `${fd} ${MONTHS_GENITIVE[fm - 1]} ${fy}`;
  if (fy !== ty) return `${fd} ${MONTHS_GENITIVE[fm - 1]} ${fy} – ${td} ${MONTHS_GENITIVE[tm - 1]} ${ty}`;
  if (fm !== tm) return `${fd} ${MONTHS_GENITIVE[fm - 1]} – ${td} ${MONTHS_GENITIVE[tm - 1]} ${ty}`;
  return `${fd} – ${td} ${MONTHS_GENITIVE[tm - 1]} ${ty}`;
}

/** Notatka kafelka „Łącznie”: „godzin we wrześniu”, „godzin w tym tygodniu”, „godzin w zakresie”. */
export function periodNote(period: TimeReportPeriod, range: { from: LocalDate; to: LocalDate }): string {
  if (period === 'this_week') return 'godzin w tym tygodniu';
  const [fy, fm, fd] = parts(range.from);
  const [ty, tm] = parts(range.to);
  if (fd === 1 && fy === ty && fm === tm && addDaysLocal(range.to, 1).slice(8) === '01') {
    return `godzin ${MONTHS_LOCATIVE[fm - 1]}`;
  }
  return 'godzin w wybranym okresie';
}

/** Podpis osi wykresu: „1 wrz” dla pierwszego dnia, potem sam dzień miesiąca. */
export function axisLabel(date: LocalDate, first: boolean): string {
  const [, m, d] = parts(date);
  return first || d === 1 ? `${d} ${MONTHS_SHORT[m - 1]}` : String(d);
}

/** Dni z podpisem na osi: pierwszy, co 7 dni i ostatni (tydzień: każdy dzień). */
export function axisTicks(days: LocalDate[]): Set<number> {
  const out = new Set<number>();
  if (days.length === 0) return out;
  if (days.length <= 7) {
    for (let i = 0; i < days.length; i++) out.add(i);
    return out;
  }
  for (let i = 0; i < days.length; i += 7) out.add(i);
  const last = days.length - 1;
  // ostatni dzień zamiast zbyt bliskiego poprzedniego podpisu
  for (const i of [...out]) if (last - i > 0 && last - i < 3) out.delete(i);
  out.add(last);
  return out;
}

/** Krótki opis dnia na osi tygodnia: „pn 28”. */
export function weekAxisLabel(date: LocalDate): string {
  return `${WEEKDAYS[weekday(date)]} ${parts(date)[2]}`;
}

/**
 * Czas trwania wpisany ręcznie: „1:40”, „1h40”, „1 h 40 min”, „1,5” (godziny), „90m”, „90 min”, „2h”.
 * Zwraca sekundy albo null.
 */
export function parseDurationInput(raw: string): number | null {
  const text = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!text) return null;
  let m = /^(\d{1,2}):(\d{1,2})$/.exec(text);
  if (m) return Number(m[1]) * 3600 + Number(m[2]) * 60;
  m = /^(\d{1,2}) ?h(?: ?(\d{1,2}) ?(?:m|min)?)?$/.exec(text);
  if (m) return Number(m[1]) * 3600 + Number(m[2] ?? 0) * 60;
  m = /^(\d{1,4}) ?(?:m|min)$/.exec(text);
  if (m) return Number(m[1]) * 60;
  m = /^(\d{1,2})(?:[.,](\d{1,2}))?$/.exec(text);
  if (m) return Math.round((Number(`${m[1]}.${m[2] ?? 0}`) * 3600) / 60) * 60;
  return null;
}

export interface TimingPreview {
  /** sekundy, gdy czas da się policzyć */
  seconds: number | null;
  /** koniec przez północ bez wskazanego dnia końca */
  needsEndDate: boolean;
  /** komunikat do pokazania przy polu (null = OK albo brak danych) */
  error: string | null;
  errorPath: string | null;
}

/** Podgląd czasu w formularzu: te same reguły co zapis (`resolveEntryTiming`). */
export function previewTiming(input: {
  date: LocalDate;
  start: string;
  end: string;
  endDate: LocalDate | null;
}): TimingPreview {
  if (!input.date || !input.start || !input.end) {
    return { seconds: null, needsEndDate: false, error: null, errorPath: null };
  }
  try {
    const r = resolveEntryTiming({ date: input.date, start: input.start, end: input.end, endDate: input.endDate });
    return { seconds: r.durationSeconds, needsEndDate: false, error: null, errorPath: null };
  } catch (err) {
    const details = (err as { details?: { crossesMidnight?: boolean; issues?: { path?: string }[] } }).details;
    return {
      seconds: null,
      needsEndDate: Boolean(details?.crossesMidnight),
      error: (err as Error).message,
      errorPath: details?.issues?.[0]?.path ?? 'end',
    };
  }
}

/** Koniec = początek + czas trwania (lokalnie, z dniem końca przy przejściu przez północ). */
export function endFromDuration(
  date: LocalDate,
  start: string,
  seconds: number,
): { end: string; endDate: LocalDate | null } | null {
  try {
    const r = resolveEntryTiming({
      date,
      start,
      durationSeconds: seconds,
      endDate: null,
    });
    return { end: r.end, endDate: null };
  } catch {
    // przez północ: dzień końca = następny dzień
    try {
      const next = addDaysLocal(date, 1);
      const r = resolveEntryTiming({ date, start, durationSeconds: seconds, endDate: next });
      return { end: r.end, endDate: r.endDate === date ? null : r.endDate };
    } catch {
      return null;
    }
  }
}

/** Czas do wyświetlenia: „8:00” albo „—”. */
export function clockOrDash(seconds: number | null): string {
  return seconds === null ? '—' : formatClock(seconds);
}

/** Dni pomiędzy (włącznie), do wykresu własnego zakresu. */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  return diffDaysLocal(from, to) + 1;
}

/** Adres eksportu CSV / PDF (REST, ten sam zestaw parametrów co raport). */
export function exportUrl(
  format: 'csv' | 'pdf',
  params: {
    period: TimeReportPeriod;
    from?: string;
    to?: string;
    scope: 'mine' | 'team';
    clientId?: string | null;
    projectId?: string | null;
    userId?: string;
  },
): string {
  const q = new URLSearchParams();
  q.set('period', params.period);
  if (params.period === 'custom') {
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
  }
  q.set('scope', params.scope);
  if (params.clientId !== undefined) q.set('clientId', params.clientId ?? 'none');
  if (params.projectId !== undefined) q.set('projectId', params.projectId ?? 'none');
  if (params.userId) q.set('userId', params.userId);
  return `/api/exports/time.${format}?${q.toString()}`;
}

/**
 * Kwoty sumy czasu jako lista walut (waluty się nie sumuje): kwota w `currency` + `otherCurrencies`. Bez kwot
 * (brak „Widzi stawki i kwoty”) — null.
 */
export function totalsAmounts(t: TimeTotals, key: 'amount' | 'unbilledAmount'): CurrencyAmount[] | null {
  const main = t[key];
  if (main === undefined) return null;
  const others = (t.otherCurrencies ?? []).map((o) => ({ currency: o.currency, amount: o[key] }));
  const list = [{ currency: t.currency ?? 'PLN', amount: main }, ...others];
  const nonZero = list.filter((c) => !/^-?0+(\.0+)?$/.test(c.amount));
  return nonZero.length > 0 ? nonZero : list.slice(0, 1);
}

/** „720 zł”, „1 200 zł + 849,99 EUR” (bez groszy, gdy kwota jest całkowita). */
export function amountsText(list: readonly CurrencyAmount[]): string {
  return formatAmounts(list, { decimals: 'auto' });
}
